import React from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import AisleController from '../business/aisle-controller';
import {getConfig} from '../config';
import sleep from '../util/sleep';
import {AISLE_ON_DURATION_MS} from '../util/constant';

const INITIAL_STATE = {
    isAutoButtonPressed: false,
    colorByAisle: new Map(),
    queryMap: {}
};
const AISLE_BUTTON_STYLE = {
    minWidth: '65px',
    maxWidth: '80px',
    minHeight: '50px',
    textShadow: '-1px -1px 0 #FFFFFF, 1px -1px 0 #FFFFFF, -1px 1px 0 #FFFFFF, 1px 1px 0 #FFFFFF',
    color: '#000000'
};
const AISLE_BUTTON_CLASS_NAME = 'p-0';
const GREY_RGB_COLOR = '#DDDDDD';
const COLOR_MAP = {
    red: 'FF0000',
    green: '00FF00',
    blue: '0000FF',
    grey: 'FFFFFF'
};
const COLOR_NAMES = Object.keys(COLOR_MAP);
const DEFAULT_COLOR_NAME = 'green';
const QUERY_PARAMETERS = {
    color: 'color',
    ledController: 'ledController'
};
const DEFAULT_ROW_LENGTH = 4;
const DEFAULT_TOTAL_PORTS = 48;

class ControllerTesterWithSiteConfig extends React.Component {
    constructor(props) {
        super(props);
        this.config = getConfig();
        this.state = Object.assign({color: DEFAULT_COLOR_NAME}, INITIAL_STATE);
        this.isAutoRunning = false;
        this.initializeAisleController();
    }

    componentDidUpdate(prevProps) {
        if (this.props.siteId !== prevProps.siteId) {
            this.setState(INITIAL_STATE);
            this.isAutoRunning = false;
            this.setQueryParameter(QUERY_PARAMETERS.ledController, undefined);
            return;
        }

        // if site config becomes available or when site config is available and color or aisle changes, re-create aisle controller
        const validControllers = ControllerTesterWithSiteConfig.getLedControllerList(this.props);
        const color = this.getColor(this.props);
        const controller = this.getController(this.props);
        const prevValidControllers = ControllerTesterWithSiteConfig.getLedControllerList(prevProps);
        const prevColor = this.getColor(prevProps);
        const prevController = this.getController(prevProps);

        if (validControllers !== prevValidControllers || color !== prevColor || controller !== prevController) {
            this.initializeAisleController();
        }
    }

    getController() {
        return this.state.queryMap[QUERY_PARAMETERS.ledController];
    }

    getColor() {
        return this.state.queryMap[QUERY_PARAMETERS.color] ? this.state.queryMap[QUERY_PARAMETERS.color] : DEFAULT_COLOR_NAME;
    }

    static getLedControllerList(props) {
        const ledControllers = ControllerTesterWithSiteConfig.getControllerConfig(props);
        return Object.keys(ledControllers);
    }

    static getControllerConfig(props) {
        if (props.siteConfig.status === 200) {
            return props.siteConfig.data.controllerConfig.ledControllers;
        }
        return null;
    }

    initializeAisleController() {
        const validControllers = ControllerTesterWithSiteConfig.getControllerConfig(this.props);
        const color = this.getColor(this.props);
        const controller = this.getController(this.props);
        const ledController = validControllers[controller];
        if (validControllers != null && ledController != null && color != null) {
            this.aisleController = new AisleController(this.props.siteId, ledController.ip);
        } else {
            this.aisleController = null;
        }
    }

    verifyQueryParametersAndRedirect() {
        const color = this.getColor(this.props);
        const controller = this.getController(this.props);
        const validControllers = ControllerTesterWithSiteConfig.getLedControllerList(this.props);

        if (!COLOR_NAMES.includes(color)) {
            this.setQueryParameter(QUERY_PARAMETERS.color, DEFAULT_COLOR_NAME);
        }
        if (validControllers != null && controller != null && !validControllers.includes(controller)) {
            this.setQueryParameter(QUERY_PARAMETERS.ledController, undefined);
        }
    }

    setQueryParameter(parameter, value) {
        const map = this.state.queryMap;
        map[parameter] = value;
        this.setState({queryMap: map});
    }

    setAisleOn(portId, color) {
        const {colorByAisle} = this.state;
        colorByAisle.set(portId, color);
        this.setState({colorByAisle});
    }

    setAisleOff(portId) {
        const {colorByAisle} = this.state;
        colorByAisle.delete(portId);
        this.setState({colorByAisle});
    }

    async blinkAisle(portId) {
        const color = this.getColor(this.props);
        const colorRgb = COLOR_MAP[color];
        await this.aisleController.start([portId], color, AISLE_ON_DURATION_MS);
        this.setAisleOn(portId, `#${colorRgb}`);
        setTimeout(() => {
            this.setAisleOff(portId);
        }, AISLE_ON_DURATION_MS);
    }

    changeController(controller) {
        this.isAutoRunning = false;
        this.setQueryParameter(QUERY_PARAMETERS.ledController, controller);
    }

    handleColorSelection(event) {
        const color = event.target.value;
        this.setQueryParameter('color', color);
    }

    handleControllerSelection(event) {
        const controller = event.target.value === '' ? null : event.target.value;
        this.changeController(controller);
    }

    handleAutoButtonClick() {
        if (!this.isAutoRunning) {
            this.startAutoRun();
        } else {
            this.stopAutoRun();
        }
    }

    async startAutoRun() {
        const controller = this.getController(this.props);
        this.isAutoRunning = true;
        this.setState({isAutoButtonPressed: true});
        const aisles = this.getAisles();
        for (const aisle of aisles) {
            if (!this.isAutoRunning || this.getController(this.props) !== controller) {
                return;
            }
            this.blinkAisle(aisle.portId);
            await sleep(AISLE_ON_DURATION_MS);
        }
        this.stopAutoRun();
    }

    stopAutoRun() {
        this.isAutoRunning = false;
        this.setState({isAutoButtonPressed: false});
    }

    getAisles() {
        if (this.getController(this.props) != null) {
            const controllerConfig = ControllerTesterWithSiteConfig.getControllerConfig(this.props);
            const controller = this.getController(this.props);
            return controllerConfig[controller].aisles ? controllerConfig[controller].aisles.flat(1) : this.generateDefaultAisles();
        }
        return [];
    }

    getControllerIndex(controller) {
        const controllers = ControllerTesterWithSiteConfig.getLedControllerList(this.props);
        if (controller != null) {
            const controllerIndex = controllers.indexOf(controller);
            return controllerIndex === -1 ? null : controllerIndex;
        }
        return null;
    }

    isAutoReady() {
        if (this.props.siteId == null || this.getController(this.props) == null) {
            return false;
        } else if (!this.isAutoRunning && this.state.colorByAisle.size > 0) {
            return false;
        }
        return true;
    }

    generateControllerOptions() {
        const controllers = ControllerTesterWithSiteConfig.getLedControllerList(this.props);
        const controllerConfig = ControllerTesterWithSiteConfig.getControllerConfig(this.props);
        const controllerOptions = [<option key='' value=''>Choose a Led Controller</option>];
        for (let i = 0; i < controllers.length; i += 1) {
            const controller = controllers[i];
            let controllerLabel = controller;
            if (controllerConfig[controller].aisles) {
                const aisles = controllerConfig[controller].aisles;
                controllerLabel = `${controllerLabel} (${aisles[0].aisle}-${aisles[aisles.length - 1].aisle})`;
            }
            controllerOptions.push(<option key={controller} value={controller}>{controllerLabel}</option>);
        }
        return controllerOptions;
    }

    generateColorOptions() {
        return COLOR_NAMES.map((colorName, index) => <option key={index}>{colorName}</option>);
    }

    generateConnectivityWarning() {
        const controllerConfig = ControllerTesterWithSiteConfig.getControllerConfig(this.props);
        const controller = this.getController(this.props);
        if (controller == null) {
            return null;
        } else if (controllerConfig == null || controllerConfig[controller] == null) {
            return <Alert variant="warning">Connectivity info is not available</Alert>;
        } else if (controllerConfig[controller].ip == null) {
            return <Alert variant="danger">{`LED controller ${controller} is offline.`}</Alert>;
        }
        return null;
    }

    generateAislesGrid() {
        const controllerConfig = ControllerTesterWithSiteConfig.getControllerConfig(this.props);
        const controller = this.getController(this.props);
        if (controller == null) {
            return {aislesGrid: <div>Select a led controller to begin</div>};
        } else if (controllerConfig[controller] == null) {
            return {aislesGrid: <div>Cannot find configuration for controller {controller}. Please contact SBL team.</div>};
        }
        let aisles = controllerConfig[controller].aisles;

        const aisleButtonRows = [];
        if (aisles == null) {
            aisles = this.generateDefaultAisles();
        }
        const {regButtons} = this.generateGrid(aisles);

        for (let i = 0; i < regButtons.length; i += 1) {
            const buttonsInRow = regButtons[i];
            aisleButtonRows.push(<ButtonGroup key={i}>{buttonsInRow}</ButtonGroup>);
        }
        return {
            aislesGrid: <ButtonGroup vertical>{aisleButtonRows}</ButtonGroup>
        };
    }

    generateDefaultAisles() {
        const aisles = [];
        for (let i = 0; i < DEFAULT_TOTAL_PORTS; i++) {
            const aisle = {
                portId: i + 1
            };
            aisles.push(aisle);
        }
        return aisles;
    }

    generateGrid(aisles) {
        let button;
        let curIndex = 0;
        let buttonsInRow = [];
        const regButtons = [];
        for (const aisle of aisles) {
            if (curIndex % DEFAULT_ROW_LENGTH === 0) {
                if (buttonsInRow.length > 0) {
                    regButtons.push(buttonsInRow);
                    buttonsInRow = [];
                }
            }
            button = this.generateAisleButton(aisle);
            buttonsInRow.push(button);
            curIndex += 1;
        }

        if (buttonsInRow.length > 0) {
            while (buttonsInRow.length < DEFAULT_ROW_LENGTH) {
                buttonsInRow.push(this.generateEmptyButton(`${buttonsInRow.length}`));
            }
            regButtons.push(buttonsInRow);
        }

        return {regButtons};
    }

    generateEmptyButton(key) {
        return <Button className={AISLE_BUTTON_CLASS_NAME}
            style={AISLE_BUTTON_STYLE}
            key={key}
            variant={'link'}
            disabled></Button>;
    }

    generateAisleButton(aisle) {
        const backgroundColor = this.state.colorByAisle.get(aisle.portId) || GREY_RGB_COLOR;
        const buttonLabel = aisle.aisle ? `${aisle.aisle}` : `Port${aisle.portId}`;
        return <Button className={AISLE_BUTTON_CLASS_NAME}
            style={{...AISLE_BUTTON_STYLE, backgroundColor}}
            variant='outline-dark'
            key={buttonLabel}
            name={buttonLabel}
            onClick={() => this.blinkAisle(aisle.portId)}>{buttonLabel}</Button>;
    }

    generateAutoButton() {
        const variant = this.state.isAutoButtonPressed ? 'danger' : 'outline-success';
        const buttonLabel = this.state.isAutoButtonPressed ? 'Stop' : 'Auto';
        return <Button className="ml-2 mr-2"
            variant={variant}
            onClick={this.handleAutoButtonClick.bind(this)}
            disabled={!this.isAutoReady()}>{buttonLabel}</Button>;
    }

    generatePrevControllerButton() {
        const controllers = ControllerTesterWithSiteConfig.getLedControllerList(this.props);
        const currentController = this.getController(this.props);
        const controllerIndex = this.getControllerIndex(currentController);
        if (controllerIndex == null || controllerIndex === 0) {
            return <Button className="ml-2 mr-2"
                variant='link'
                disabled={true}
                active={false}></Button>;
        }
        const prevControllerIndex = controllerIndex - 1;
        const prevControllerLabel = controllers[prevControllerIndex];
        return <Button className="ml-2 mr-2"
            variant="warning"
            onClick={() => this.changeController(prevControllerLabel)}>{'< prev controller'}</Button>;
    }

    generateNextControllerButton() {
        const controllers = ControllerTesterWithSiteConfig.getLedControllerList(this.props);
        const currentController = this.getController(this.props);
        const controllerIndex = this.getControllerIndex(currentController);
        if (controllerIndex === controllers.length - 1) {
            return <Button className="ml-2 mr-2"
                variant='link'
                disabled={true}
                active={false}></Button>;
        }
        const nextControllerIndex = controllerIndex == null ? 0 : controllerIndex + 1;
        const nextControllerLabel = controllers[nextControllerIndex];
        return <Button className="ml-2 mr-2"
            variant="warning"
            onClick={() => this.changeController(nextControllerLabel)}>{'next controller >'}</Button>;
    }

    render() {
        const {aislesGrid} = this.generateAislesGrid();
        const controller = this.getController(this.props);
        return <div>
            <Container>
                <Row className="mt-3" >
                    <Col>
                        <Form.Group controlId="color">
                            <Form.Label>Color</Form.Label>
                            <Form.Control as="select"
                                name="color"
                                value={this.getColor(this.props)}
                                onChange={this.handleColorSelection.bind(this)}
                            >
                                {this.generateColorOptions()}
                            </Form.Control>
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Group controlId="aisle">
                            <Form.Label>Led Controller</Form.Label>
                            <Form.Control as="select"
                                name="controller"
                                value={controller == null ? '' : controller}
                                onChange={this.handleControllerSelection.bind(this)}
                            >
                                {this.generateControllerOptions()}
                            </Form.Control>
                        </Form.Group>
                    </Col>
                </Row>
                {this.generateConnectivityWarning()}
                <Row style={{overflowX: 'auto', whiteSpace: 'nowrap'}}>
                    <Col>{aislesGrid}</Col>
                </Row>
                <Row className="justify-content-md-center mt-3 mb-3">
                    <Col>
                        {this.generatePrevControllerButton()}
                        {this.generateAutoButton()}
                        {this.generateNextControllerButton()}
                    </Col>
                </Row>
            </Container>
        </div>;
    }
}

export default ControllerTesterWithSiteConfig;



// WEBPACK FOOTER //
// ./src/components/devicemanagement/ledcontroller/bintester/component/controller-tester-with-site-config.jsx